import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { servicesAPI, publicAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { 
  ArrowLeft, CheckCircle, ShoppingCart, RefreshCw, User, Lock, Mail,
  Server, Globe, Zap, Shield, Headphones, TrendingUp, Inbox, Star,
  Monitor, Smartphone, Laptop, Cpu, Database, Cloud,
  Code, Terminal, Settings, Wifi, Key,
  Image, Video, Music, Mic, Camera, MessageSquare, Phone,
  Send, Bell, Megaphone, Radio, Tv,
  File, FileText, Folder, Archive, Clipboard, BookOpen, Newspaper,
  CreditCard, DollarSign, Package, Truck, Tag, Receipt, Wallet,
  Users, Heart, ThumbsUp, Share, Globe2,
  Wrench, Hammer, Paintbrush, Scissors, Layers, Grid, Layout,
  Award, Target, Flag, Bookmark, Calendar, Clock, MapPin, Eye, Edit, Plus, BarChart, PieChart,
  ArrowRight, Sparkles
} from 'lucide-react';

const BACKEND_URL = '';

// Extended Icon mapping - matches IconSelector
const iconMap = {
  TrendingUp, Globe, Zap, Mail, Server, ShoppingCart, Inbox,
  Shield, Headphones, Star, CheckCircle, BarChart, PieChart,
  Monitor, Smartphone, Laptop, Cpu, Database, Cloud,
  Code, Terminal, Settings, Wifi, Lock, Key, RefreshCw,
  Image, Video, Music, Mic, Camera, MessageSquare, Phone,
  Send, Bell, Megaphone, Radio, Tv,
  File, FileText, Folder, Archive, Clipboard, BookOpen, Newspaper,
  CreditCard, DollarSign, Package, Truck, Tag, Receipt, Wallet,
  Users, User, Heart, ThumbsUp, Share, Globe2,
  Wrench, Hammer, Paintbrush, Scissors, Layers, Grid, Layout,
  Award, Target, Flag, Bookmark, Calendar, Clock, MapPin, Eye, Edit, Plus
};

const getIcon = (iconName) => iconMap[iconName] || Server;

// Logo helpers
const getLogoUrl = (settings) => {
  if (!settings?.logo_url) return null;
  if (settings.logo_url.startsWith('http')) return settings.logo_url;
  return `${BACKEND_URL}${settings.logo_url}`;
};

const getLogoInitials = (settings) => {
  const name = settings?.site_name || 'Edrano';
  return name.substring(0, 2).toUpperCase();
};

// Category labels and icons
const categoryLabels = {
  google_ads: 'Google Ads',
  meta_ads: 'Meta Ads',
  wordpress_support: 'WordPress Support',
  wordpress_upgrades: 'WordPress Upgrades',
  woocommerce_upgrades: 'WooCommerce',
  newsletter: 'Newsletter',
  accessibility: 'Accessibility',
  hosting: 'Hosting',
  email_client: 'Email Services',
};

const categoryIcons = {
  google_ads: 'TrendingUp',
  meta_ads: 'Globe',
  wordpress_support: 'Headphones',
  wordpress_upgrades: 'Zap',
  woocommerce_upgrades: 'ShoppingCart',
  newsletter: 'Mail',
  accessibility: 'Shield',
  hosting: 'Server',
  email_client: 'Inbox',
};

export default function ServiceDetailPublic() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [service, setService] = useState(null);
  const [recommended, setRecommended] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState(null);
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);

  useEffect(() => {
    loadService();
  }, [slug]);

  const loadService = async () => {
    try {
      const [serviceRes, settingsRes] = await Promise.all([
        servicesAPI.getBySlug(slug),
        siteAPI.getSiteSettings()
      ]);
      setService(serviceRes.data);
      setSettings(settingsRes.data);
      
      if (serviceRes.data.pricing_tiers?.length > 0) {
        setSelectedTier(serviceRes.data.pricing_tiers[0]);
      }
      
      try {
        const recRes = await servicesAPI.getRecommended(serviceRes.data.id);
        setRecommended(recRes.data);
      } catch (e) {
        console.log('No recommendations available');
      }
    } catch (error) {
      console.error('Failed to load service:', error);
      toast.error('Service not found');
      navigate('/services');
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNow = () => {
    if (user) {
      navigate('/cart', { 
        state: { 
          addItem: {
            service_id: service.id,
            name: service.name,
            tier_name: selectedTier.name,
            price: selectedTier.price,
            quantity: 1
          }
        }
      });
    } else {
      setShowCheckoutDialog(true);
    }
  };

  const getServiceSlug = (s) => {
    return s.name.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-');
  };

  const getLowestPrice = (s) => {
    if (!s.pricing_tiers?.length) return null;
    return Math.min(...s.pricing_tiers.map(t => t.price));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-100">
        <h1 className="text-2xl font-heading font-bold mb-4">Service Not Found</h1>
        <Link to="/services">
          <Button className="btn-gradient text-white rounded-full">Browse Services</Button>
        </Link>
      </div>
    );
  }

  const Icon = getIcon(service.icon || categoryIcons[service.category]);
  const hasImage = service.image_url && service.image_url.length > 0;
  const imageUrl = hasImage 
    ? (service.image_url.startsWith('http') ? service.image_url : `${BACKEND_URL}${service.image_url}`)
    : null;

  return (
    <div className="page-wrapper" data-testid="service-detail-page">
      <div className="container-main mx-auto">
        {/* Header */}
        <header className="bg-slate-900 rounded-2xl mb-3 md:mb-4 sticky top-3 md:top-4 z-50 shadow-xl shadow-slate-900/20">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/30 overflow-hidden">
                  {settings?.logo_url ? (
                    <img 
                      src={getLogoUrl(settings)} 
                      alt={settings?.site_name || 'Logo'}
                      className="w-full h-full object-contain p-1"
                    />
                  ) : (
                    <span className="text-white font-bold text-lg">{getLogoInitials(settings)}</span>
                  )}
                </div>
                <span className="font-heading font-bold text-xl text-white">{settings?.site_name || 'Edrano'}</span>
              </Link>
              <div className="flex items-center space-x-3">
                {user ? (
                  <Link to="/dashboard">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-slate-300 hover:text-white hover:bg-white/10 rounded-full"
                      data-testid="dashboard-btn"
                    >
                      Dashboard
                    </Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/login">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-slate-300 hover:text-white hover:bg-white/10 rounded-full"
                        data-testid="login-btn"
                      >
                        Sign In
                      </Button>
                    </Link>
                    <Link to="/register">
                      <Button 
                        size="sm" 
                        className="btn-gradient text-white rounded-full px-5 shadow-lg shadow-indigo-500/30"
                        data-testid="register-btn"
                      >
                        Get Started
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="bg-white rounded-3xl p-4 sm:p-6 lg:p-8">
          {/* Back Button */}
          <div className="mb-6">
            <Link 
              to="/services" 
              className="inline-flex items-center text-sm text-slate-500 hover:text-indigo-600 transition-colors"
              data-testid="back-services-link"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Services
            </Link>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Service Info - Left Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Hero Card */}
              <div className="bg-slate-50 rounded-2xl p-6 md:p-8">
                <div className="flex items-start gap-5 mb-6">
                  {/* Icon/Image */}
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-500/10 to-violet-500/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {hasImage ? (
                      <img 
                        src={imageUrl} 
                        alt={service.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Icon className="h-10 w-10 text-indigo-600" />
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <Badge className="bg-indigo-50 text-indigo-700 hover:bg-indigo-50 border border-indigo-100 mb-3">
                      {categoryLabels[service.category] || service.category}
                    </Badge>
                    <h1 className="text-2xl md:text-3xl font-heading font-bold text-slate-900" data-testid="service-title">
                      {service.name}
                    </h1>
                  </div>
                </div>

                {service.short_description && (
                  <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                    {service.short_description}
                  </p>
                )}
                
                {service.description && (
                  <div className="prose prose-slate prose-lg max-w-none">
                    <p className="text-slate-600">{service.description}</p>
                  </div>
                )}
              </div>

              {/* Features Card */}
              {service.features?.length > 0 && (
                <div className="bg-slate-50 rounded-2xl p-6 md:p-8">
                  <h2 className="text-xl font-heading font-bold text-slate-900 mb-6 flex items-center">
                    <Sparkles className="h-5 w-5 mr-2 text-indigo-600" />
                    What's Included
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {service.features.map((feature, i) => (
                      <div 
                        key={i} 
                        className="flex items-start gap-3 p-4 bg-white rounded-xl hover:bg-indigo-50/50 transition-colors"
                        data-testid={`feature-${i}`}
                      >
                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </div>
                        <span className="text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Pricing Sidebar - Right Column */}
            <div className="lg:col-span-1">
              <div className="bg-slate-50 rounded-2xl p-6 sticky top-24">
                <h2 className="text-xl font-heading font-bold text-slate-900 mb-6">Choose Your Plan</h2>
                
                <RadioGroup 
                  value={selectedTier?.name || ''} 
                  onValueChange={(value) => {
                    const tier = service.pricing_tiers.find(t => t.name === value);
                    setSelectedTier(tier);
                  }}
                  className="space-y-3"
                >
                  {service.pricing_tiers?.map((tier) => (
                    <label
                      key={tier.name}
                      className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedTier?.name === tier.name 
                          ? 'border-indigo-500 bg-white shadow-sm' 
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid={`tier-${tier.name}`}
                    >
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value={tier.name} className="sr-only" />
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                          selectedTier?.name === tier.name ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'
                        }`}>
                          {selectedTier?.name === tier.name && (
                            <div className="w-2 h-2 rounded-full bg-white" />
                          )}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">{tier.name}</div>
                          {tier.duration_months && (
                            <div className="text-sm text-slate-500">
                              {tier.duration_months} month{tier.duration_months > 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-xl font-bold text-indigo-600">
                        €{tier.price.toFixed(2)}
                      </div>
                    </label>
                  ))}
                </RadioGroup>

                {/* Tier Features */}
                {selectedTier?.features?.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <h3 className="font-medium text-slate-900 mb-3">Plan Includes:</h3>
                    <ul className="space-y-2">
                      {selectedTier.features.map((feature, i) => (
                        <li key={i} className="flex items-center text-sm text-slate-600">
                          <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Total & CTA */}
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-slate-600">Total</span>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-slate-900">
                        €{selectedTier?.price.toFixed(2) || '0.00'}
                      </div>
                      <div className="text-xs text-slate-500">+ 24% VAT</div>
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full h-12 text-base btn-gradient text-white rounded-xl shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/40 hover:scale-[1.01] active:scale-[0.99] transition-all" 
                    size="lg"
                    onClick={handleBuyNow}
                    disabled={!selectedTier}
                    data-testid="buy-now-btn"
                  >
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    {user ? 'Add to Cart' : 'Buy Now'}
                  </Button>
                  
                  {!user && (
                    <p className="text-xs text-center text-slate-500 mt-4">
                      Quick checkout - create account & pay in one step
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Recommended Services */}
          {recommended.length > 0 && (
            <div className="mt-12 pt-8 border-t border-slate-100">
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-6">You might also like</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {recommended.map((rec) => {
                  const RecIcon = getIcon(rec.icon || categoryIcons[rec.category]);
                  const lowestPrice = getLowestPrice(rec);
                  const recSlug = getServiceSlug(rec);
                  
                  return (
                    <Link 
                      key={rec.id} 
                      to={`/service/${recSlug}`}
                      className="group"
                      data-testid={`recommended-${rec.id}`}
                    >
                      <Card className="h-full bg-slate-50 border-slate-100 rounded-2xl overflow-hidden card-hover">
                        <CardContent className="p-5">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500/10 to-violet-500/10 flex items-center justify-center mb-4 group-hover:from-indigo-500/20 group-hover:to-violet-500/20 transition-colors">
                            <RecIcon className="h-6 w-6 text-indigo-600" />
                          </div>
                          <h3 className="font-heading font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors mb-2 line-clamp-2">
                            {rec.name}
                          </h3>
                          <Badge className="bg-white text-slate-600 hover:bg-white border border-slate-200 font-medium text-xs mb-3">
                            {categoryLabels[rec.category] || rec.category}
                          </Badge>
                          {lowestPrice && (
                            <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                              <span className="text-sm text-slate-500">From</span>
                              <span className="text-lg font-bold text-indigo-600">
                                €{lowestPrice.toFixed(0)}
                              </span>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Checkout Dialog */}
      <GuestCheckoutDialog
        open={showCheckoutDialog}
        onOpenChange={setShowCheckoutDialog}
        service={service}
        selectedTier={selectedTier}
      />
    </div>
  );
}

function GuestCheckoutDialog({ open, onOpenChange, service, selectedTier }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const checkoutData = {
        ...formData,
        items: [{
          service_id: service.id,
          name: service.name,
          tier_name: selectedTier.name,
          price: selectedTier.price,
          quantity: 1
        }]
      };

      const res = await publicAPI.guestCheckout(checkoutData);
      
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify({
        id: res.data.user_id,
        email: formData.email,
        full_name: formData.full_name,
        role: 'client'
      }));

      window.open(res.data.checkout_url, '_blank');
      
      toast.success('Account created! Redirecting to payment...');
      onOpenChange(false);
      
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1000);

    } catch (error) {
      const message = error.response?.data?.detail || 'Checkout failed. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const vatAmount = selectedTier ? selectedTier.price * 0.24 : 0;
  const total = selectedTier ? selectedTier.price + vatAmount : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Quick Checkout</DialogTitle>
          <DialogDescription>
            Create your account and complete purchase
          </DialogDescription>
        </DialogHeader>

        {/* Order Summary */}
        <div className="bg-slate-50 rounded-xl p-4 mb-4">
          <div className="flex justify-between mb-2">
            <span className="text-slate-600">{service?.name}</span>
            <span className="font-semibold text-slate-900">€{selectedTier?.price.toFixed(2)}</span>
          </div>
          <div className="flex justify-between mb-2 text-sm text-slate-500">
            <span>{selectedTier?.name}</span>
          </div>
          <div className="flex justify-between text-sm text-slate-500">
            <span>VAT (24%)</span>
            <span>€{vatAmount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between pt-3 mt-3 border-t border-slate-200">
            <span className="font-semibold text-slate-900">Total</span>
            <span className="font-bold text-xl text-indigo-600">€{total.toFixed(2)}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
              {error}
            </div>
          )}

          <div>
            <Label htmlFor="full_name" className="flex items-center gap-2 text-sm font-medium">
              <User className="h-4 w-4 text-slate-400" />
              Full Name
            </Label>
            <Input
              id="full_name"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              placeholder="John Doe"
              required
              className="mt-1.5 rounded-xl"
              data-testid="checkout-name-input"
            />
          </div>

          <div>
            <Label htmlFor="email" className="flex items-center gap-2 text-sm font-medium">
              <Mail className="h-4 w-4 text-slate-400" />
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="john@example.com"
              required
              className="mt-1.5 rounded-xl"
              data-testid="checkout-email-input"
            />
          </div>

          <div>
            <Label htmlFor="password" className="flex items-center gap-2 text-sm font-medium">
              <Lock className="h-4 w-4 text-slate-400" />
              Create Password
            </Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="••••••••"
              minLength={6}
              required
              className="mt-1.5 rounded-xl"
              data-testid="checkout-password-input"
            />
            <p className="text-xs text-slate-500 mt-1.5">At least 8 characters with uppercase and special character</p>
          </div>

          <DialogFooter className="gap-3 sm:gap-0 pt-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="rounded-full"
              data-testid="checkout-cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={submitting}
              className="btn-gradient text-white rounded-full px-6"
              data-testid="checkout-submit-btn"
            >
              {submitting ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CreditCard className="h-4 w-4 mr-2" />
              )}
              Continue to Payment
            </Button>
          </DialogFooter>
        </form>

        <p className="text-xs text-center text-slate-500 pt-2">
          Already have an account?{' '}
          <Link to="/login" className="text-indigo-600 hover:underline font-medium">
            Sign in
          </Link>
        </p>
      </DialogContent>
    </Dialog>
  );
}
